type AppConfigValue = string | number | boolean | null | undefined | AppConfigMarketingSite;
type AppConfigKey =
  | 'env'
  | 'isLocal'
  | 'isProduction'
  | 'baseUrl'
  | 'googleAnalyticsId'
  | 'linktrUrl'
  | 'mapboxToken'
  | 'marketingSite'
  | 'redirectUsersWithLapsedMembershipTo';

export type AppConfigType = {
  [key in AppConfigKey]: AppConfigValue;
};

interface AppConfigMarketingSite {
  baseUrl?: string;
  aboutUsUrl?: string;
  bookingTermsConditionsUrl?: string;
  clubUrl?: string;
  contactUrl?: string;
  memberAgreementsUrl?: string;
  membershipsOptionsUrl?: string;
  privacyPoliciesUrl?: string;
  termsOfUseUrl?: string;
}

class AppConfig {
  private static instance: AppConfig;
  readonly #config: AppConfigType;

  private constructor() {
    const marketingSiteBaseUrl = process.env.VUE_APP_MARKETING_SITE_BASE_URL;

    this.#config = {
      env: process.env.NODE_ENV,
      isLocal: process.env.NODE_ENV !== 'production',
      isProduction: process.env.NODE_ENV === 'production',
      baseUrl: process.env.VUE_APP_BASE_URL,
      googleAnalyticsId: process.env.VUE_APP_GOOGLE_ANALYTICS_ID,
      linktrUrl: 'https://linktr.ee/thecultivist',
      mapboxToken: process.env.VUE_APP_MAPBOX_TOKEN,
      marketingSite: {
        baseUrl: marketingSiteBaseUrl,
        aboutUsUrl: `${marketingSiteBaseUrl}/about-us`,
        bookingTermsConditionsUrl: `${marketingSiteBaseUrl}/legal/bookings-terms-conditions`,
        clubUrl: `${marketingSiteBaseUrl}/the-club`,
        contactUrl: `${marketingSiteBaseUrl}/contact`,
        memberAgreementsUrl: `${marketingSiteBaseUrl}/legal/member-agreements`,
        membershipsOptionsUrl: `${marketingSiteBaseUrl}/memberships-options`,
        privacyPoliciesUrl: `${marketingSiteBaseUrl}/legal/privacy-policies`,
        termsOfUseUrl: `${marketingSiteBaseUrl}/legal/terms-of-use`,
      },
      redirectUsersWithLapsedMembershipTo: 'payment-details',
    };
  }

  public static getInstance(): AppConfig {
    if (!AppConfig.instance) {
      AppConfig.instance = new AppConfig();
    }
    return AppConfig.instance;
  }

  public get(key: AppConfigKey, defaultValue: AppConfigValue = undefined): AppConfigValue {
    return this.#config[key] || defaultValue;
  }

  public getAsString(key: AppConfigKey, defaultValue: AppConfigValue = undefined): string | undefined {
    const value = this.get(key, defaultValue);

    return value !== undefined ? String(value) : value;
  }

  public all(): AppConfigType {
    return this.#config;
  }
}

export default AppConfig.getInstance();
